<template>
    <div>
        <h1 class="mt-2">{{ $filters.capitalize($l10n.__('paiement')) }}</h1>
        <div class="mt-6">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('votre abonnement')) }}
                </template>
                <template #right>
                    {{ formatAmount(centsToCurrency(cart.totalAmountPerMonth)) }} /
                    {{ $l10n.__('mois') }}
                </template>
            </line-2-slots>
            <template
                v-if="cartBasicProduct.customPricing && cartBasicProduct.customPricing.length"
            >
                <line-2-slots
                    v-for="(pricing, index) of cartBasicProduct.customPricing.reverse()"
                    :key="index"
                    class="fs-3 mb-2"
                >
                    <template v-if="pricing.unit_amount === 0" #left>
                        {{
                            $l10n.n__(
                                '%(nb)s utilisateur',
                                '%(nb)s utilisateurs',
                                pricing.nbLicences,
                                { nb: pricing.nbLicences },
                            )
                        }}
                    </template>
                    <template v-else #left>
                        {{
                            $l10n.n__(
                                '%(nb)s utilisateur',
                                '%(nb)s utilisateurs',
                                pricing.nbLicences,
                                { nb: pricing.nbLicences },
                            )
                        }}
                    </template>
                    <template v-if="pricing.unit_amount === 0" #right>
                        {{ $l10n.n__('Offert', 'Offerts', pricing.nbLicences) }}
                    </template>
                    <template v-else #right>
                        {{ formatAmount(getPricingByPlan(pricing.unit_amount)) }} /
                        {{ $l10n.__('mois') }}
                    </template>
                </line-2-slots>
            </template>
            <line-2-slots v-else class="fs-3 mb-2">
                <template v-if="licenses.offered > 0" #left>
                    {{
                        $l10n.n__('%(nb)s utilisateur', '%(nb)s utilisateurs', licenses.nb, {
                            nb: licenses.nb,
                        })
                    }}
                    {{
                        $l10n.n__(
                            'dont %(offered)s offert',
                            'dont %(offered)s offerts',
                            licenses.offered,
                            { offered: licenses.offered },
                        )
                    }}
                </template>
                <template v-else #left>
                    {{
                        $l10n.n__('%(nb)s utilisateur', '%(nb)s utilisateurs', licenses.nb, {
                            nb: licenses.nb,
                        })
                    }}
                </template>
                <template #right>
                    {{ formatAmount(cartBasicProductTotalAmountPerMonth) }} / {{ $l10n.__('mois') }}
                </template>
            </line-2-slots>
            <line-2-slots v-for="product in cart.items" :key="product.id" class="fs-3 mb-2">
                <template #left>
                    {{ productName(product) }}
                </template>
                <template #right>
                    <span v-if="product == cartBasicProduct">{{
                        $filters.capitalize($l10n.__('inclus'))
                    }}</span>
                    <span v-else
                        >{{ formatAmount(centsToCurrency(product.totalAmountPerMonth)) }} /
                        {{ $l10n.__('mois') }}</span
                    >
                </template>
            </line-2-slots>
        </div>
        <!-- Don't forget to check hasVat -->
        <div class="mt-6 py-4 bt-1 bb-1 fs-3">
            <!-- New -->
            <line-2-slots v-if="isCartContext('new')" :isSubtitle="true">
                <template #left>
                    {{ $filters.capitalize($l10n.__("date d'activation")) }}
                </template>
                <template #right>
                    <span v-if="cartPeriodStartDateObj" class="red">
                        {{
                            $filters.dateFormatter(
                                cartPeriodStartDateObj.unix(),
                                'NORMAL_DATE',
                                false,
                            )
                        }}
                    </span>
                    <span v-else class="red">
                        {{ $filters.dateFormatter(now.unix(), 'NORMAL_DATE', false) }}
                    </span>
                </template>
            </line-2-slots>
            <!-- Renew -->
            <line-2-slots
                v-else-if="
                    (isCartContext('legacy_renew') || isCartContext('renew')) &&
                    cartPeriodStartDateObj
                "
                :isSubtitle="true"
            >
                <template #left>
                    {{ $l10n.__('Date de renouvellement de votre abonnement') }}
                </template>
                <template #right>
                    <span v-if="!cartEarlyRenewal" class="red">
                        {{
                            $filters.dateFormatter(
                                cartPeriodStartDateObj.unix(),
                                'NORMAL_DATE',
                                false,
                            )
                        }}
                    </span>
                    <span v-else class="red">
                        {{ $filters.dateFormatter(now.unix(), 'NORMAL_DATE', false) }}
                    </span>
                </template>
            </line-2-slots>
            <!-- Upsell -->
            <line-2-slots
                v-else-if="!isCartContext('new') && customerServiceEndDate"
                :isSubtitle="true"
            >
                <template #left>
                    {{ $l10n.__('Date de fin de votre abonnement') }}
                </template>
                <template #right>
                    <span>
                        {{
                            $filters.dateFormatter(
                                customerServiceEndDateObj.unix(),
                                'NORMAL_DATE',
                                false,
                            )
                        }}
                    </span>
                </template>
            </line-2-slots>

            <line-2-slots v-if="isCartContext('renew')">
                <template v-if="cart.paymentMethod == 'stripe_card'" #left>
                    {{
                        $filters.capitalize(
                            $l10n.__('la facture sera éditée et prélevée le %(date)s', {
                                date: $filters.dateFormatter(
                                    cartInvoiceEditionDate.unix(),
                                    'SLASHED_DATE',
                                    false,
                                ),
                            }),
                        )
                    }}
                </template>
                <template v-else #left>
                    {{
                        $filters.capitalize(
                            $l10n.__('la facture sera éditée le %(date)s', {
                                date: $filters.dateFormatter(
                                    cartInvoiceEditionDate.unix(),
                                    'SLASHED_DATE',
                                    false,
                                ),
                            }),
                        )
                    }}
                </template>
            </line-2-slots>
        </div>

        <div v-if="isMonthly" class="mt-6">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('total mensuel')) }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="showTaxTotal" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('TVA')) }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountVatPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="showTaxTotal" :isSubtitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('Total mensuel TTC')) }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountVatIncludedPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots
                v-if="cartEarlyRenewal"
                :isSubtitle="true"
                class="bt-1 pt-3 fs-3 mt-6 mb-2"
            >
                <template #left>
                    {{ $filters.capitalize($l10n.__('échéancier')) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="cartEarlyRenewal" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('Restant à consommer')) }}
                </template>
                <template #right>
                    {{ formatAmount(-1 * centsToCurrency(cartEarlyRenewalDiscount), true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-for="deadline in deadlines" :key="deadline.key" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.dateFormatter(deadline.date.unix(), 'NORMAL_DATE', false) }}
                </template>
                <template #right>
                    {{ formatAmount(deadline.amount, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="deadlineUnchanged > 0 && deadlineUnchanged < 12" class="fs-3 mb-2">
                <template #left>
                    {{
                        $l10n.__('%(deadlineUnchanged)s échéances suivantes', {
                            deadlineUnchanged: deadlineUnchanged,
                        })
                    }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountVatIncludedPerMonth, true) }}
                </template>
            </line-2-slots>
        </div>
        <div v-else class="mt-6">
            <line-2-slots v-if="cartEarlyRenewal" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('Montant HT')) }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountPerYear, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="cartEarlyRenewal" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('Restant à consommer')) }}
                </template>
                <template #right>
                    {{ formatAmount(-1 * centsToCurrency(cartEarlyRenewalDiscount), true) }}
                </template>
            </line-2-slots>

            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('total')) }}
                </template>
                <template #right>
                    {{ formatAmount(totalPreviewCart.perYearDiscounted, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="showTaxTotal" class="fs-3 mb-2">
                <template #left> {{ $l10n.__('TVA') }} ({{ countryTaxRate * 100 }}%) </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountVatPerYear, true) }}
                </template>
            </line-2-slots>
            <line-2-slots v-if="showTaxTotal" :isSubtitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('total TTC')) }}
                </template>
                <template #right>
                    {{ formatAmount(cartTotalAmountVatIncludedPerYear, true) }}
                </template>
            </line-2-slots>
            <line-2-slots v-if="isPaymentFacilityEnabled" :isSubtitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $l10n.__('Paiement en %s fois activé', cart.paymentFacility) }}
                </template>
                <template v-if="cartNextAmountFacility < cartFirstAmountFacility" #right>
                    1 x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
                <template v-else #right>
                    {{ cart.paymentFacility }} x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="isPaymentFacilityEnabled && cartNextAmountFacility < cartFirstAmountFacility"
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template #right>
                    {{ cart.paymentFacility - 1 }} x
                    {{ formatAmount(cartNextAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="isCustomPaymentFacilityEnabled"
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template #left>
                    {{ $l10n.__('Paiement en %s fois activé', cartNbCustomFacilities) }}
                </template>
                <template v-if="cartNextAmountFacility < cartFirstAmountFacility" #right>
                    {{ cartNbFirstCustomFacilities }} x
                    {{ formatAmount(cartFirstAmountCustomFacility, true) }}
                </template>
                <template v-else #right>
                    {{ cartNbCustomFacilities }} x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="
                    isCustomPaymentFacilityEnabled &&
                    cartFirstAmountCustomFacility < cartLastAmountCustomFacility
                "
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template #right>
                    1 x {{ formatAmount(cartLastAmountCustomFacility, true) }}
                </template>
            </line-2-slots>
        </div>

        <div v-if="isCartContext('upsale')" class="mt-6">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template #left>
                    {{ $filters.capitalize($l10n.__('total HT supplémentaire estimé à payer*')) }}
                </template>
                <template #right>
                    {{
                        formatAmount(
                            newItemsOnSubscriptionTotalForCurrentPricingPlan.totalDiscounted,
                        )
                    }}
                </template>
            </line-2-slots>
            <line-2-slots v-if="showTaxTotal && countryTaxRate > 0" class="fs-3 mb-2">
                <template #left>
                    {{ $l10n.__('TVA estimée') }} ({{ countryTaxRate * 100 }}%)
                </template>
                <template #right>
                    {{ formatAmount(newItemsOnSubscriptionTotalForCurrentPricingPlan.tax) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="showTaxTotal && countryTaxRate > 0"
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template #left>
                    {{ $filters.capitalize($l10n.__('total TTC supplémentaire estimé à payer*')) }}
                </template>
                <template #right>
                    {{
                        formatAmount(
                            newItemsOnSubscriptionTotalForCurrentPricingPlan.totalTaxIncluded,
                        )
                    }}
                </template>
            </line-2-slots>
        </div>

        <!-- PRORATA TEMPORIS -->
        <div v-if="isCartContext('upsale')" class="my-5">
            <p v-if="isCurrentPricingMonthly">
                <span class="semibold">{{ $l10n.__('Attention') }} :</span>
                {{
                    $l10n.__(
                        'La prochaine facture est calculée prorata temporis, les échéances suivantes au taux plein',
                    )
                }}.
            </p>
            <p v-else class="my-5">
                *
                {{
                    $l10n.__(
                        'Ce montant est une estimation pour un prorata à cet instant. Le montant exact de la facture sera calculé au prorata du temps restant sur votre abonnement au moment du paiement.',
                    )
                }}
            </p>
        </div>

        <div v-if="!hasManualPaymentMethod" class="mt-6">
            <CompassButton
                :label="
                    isCartContext('renew')
                        ? $l10n.__('valider mon renouvellement')
                        : $filters.capitalize($l10n.__('valider et payer mon abonnement'))
                "
                type="primary"
                :disabled="!clickEnabled"
                class="wp-10"
                @click="$emit('clicked')"
            />
        </div>
        <div class="mt-8 b-1 p-4 br">
            <p class="fs-3 m-0">
                {{ $l10n.__('Vous pouvez partager cette page avec le lien suivant') }}
            </p>
            <p class="mt-2 mb-0 copy-element block">
                <a class="link">{{ currentUrl }}</a>
                <SaasClipboard v-if="currentUrl" :data="currentUrl" />
            </p>
        </div>
    </div>
</template>

<script>
import SaasClipboard from 'vcomponents/utilities/SaasClipboard.vue';
import WidgetMixins from '../../mixins/sellsyaccountmanager/widgetMixins';

import Line2Slots from '../utilities/Line2slots.vue';
export default {
    components: {
        SaasClipboard,
        Line2Slots,
    },
    mixins: [WidgetMixins],
    props: {
        isMonthly: {
            type: Boolean,
            required: true,
        },
        cart: {
            type: Object,
            required: true,
        },
        clickEnabled: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasManualPaymentMethod: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        now() {
            return moment();
        },
        currentUrl() {
            return window.location.href;
        },
        cartBasicProduct() {
            return this.cart.items.find(
                (c) =>
                    c.product === this.pricingBasicProduct.id ||
                    c.product_id === this.pricingBasicProduct.id,
            );
        },
        licenses() {
            return {
                nb: this.cartBasicProduct.quantity,
                offered: this.cartBasicProduct.customQuantityFree,
            };
        },
        cartPeriodStartDateObj() {
            return this.cart.periodApplianceStart ? moment(this.cart.periodApplianceStart) : null;
        },

        cartTotalAmountPerMonth() {
            return this.totalPreviewCart.perMonth;
        },
        cartTotalAmountVatPerMonth() {
            return this.totalPreviewCart.vatPerMonth;
        },
        cartTotalAmountVatIncludedPerMonth() {
            return this.totalPreviewCart.vatIncludedPerMonth;
        },
        cartTotalAmountPerYear() {
            return this.totalPreviewCart.perYear;
        },
        cartTotalAmountVatPerYear() {
            return this.totalPreviewCart.vatPerYear;
        },
        cartTotalAmountVatIncludedPerYear() {
            return this.totalPreviewCart.vatIncludedPerYear;
        },

        isPaymentFacilityEnabled() {
            return (
                !this.isCurrentPricingMonthly &&
                !this.isCartContext('upsale') &&
                this.cart.paymentFacility > 1 &&
                this.cart.paymentFacility < 12
            );
        },
        cartFacilityAmounts() {
            return this.buildFacilityAmounts(
                this.totalPreviewCart.vatIncludedPerYear,
                this.cart.paymentFacility,
            );
        },
        cartFirstAmountFacility() {
            return this.cartFacilityAmounts.firstAmount;
        },
        cartNextAmountFacility() {
            return this.cartFacilityAmounts.nextAmount;
        },

        isCustomPaymentFacilityEnabled() {
            return (
                !this.isCurrentPricingMonthly &&
                !this.isCartContext('upsale') &&
                this.cart.paymentFacility > 10000
            );
        },
        cartCustomFacilitiesAmount() {
            return this.buildCustomFacilityAmounts(
                this.totalPreviewCart.vatIncludedPerYear,
                this.cart.paymentFacility,
            );
        },
        cartNbFirstCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities;
        },
        cartNbCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities + 1;
        },
        cartFirstAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.firstAmount;
        },
        cartLastAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.lastAmount;
        },

        totalPreviewCart() {
            const perMonth = this.centsToCurrency(this.cart.totalAmountPerMonth);
            const perYear = this.centsToCurrency(this.cart.totalAmountPerYear);
            const perYearDiscounted = this.cartEarlyRenewal
                ? perYear - this.centsToCurrency(this.cartEarlyRenewalDiscount)
                : perYear;
            const perYearTax = perYearDiscounted * this.countryTaxRate;
            return this.buildTotalPreview(
                perMonth,
                perMonth * this.countryTaxRate,
                perYear,
                perYearTax,
                null,
                perYearDiscounted,
            );
        },

        deadlines() {
            if (!this.cartEarlyRenewal) {
                return [];
            }

            let date = moment();
            let discount = this.centsToCurrency(this.cartEarlyRenewalDiscount);
            const deadlines = [];
            for (let month = 0; month < 12; month++) {
                let amount = this.cartTotalAmountPerMonth;
                date = moment(date).add(1, 'M');

                if (discount > amount) {
                    discount -= amount;
                    amount = 0;
                } else {
                    amount -= discount;
                    discount = 0;
                }

                if (amount !== this.cartTotalAmountPerMonth) {
                    deadlines.push({
                        key: month,
                        date: date.clone(),
                        amount: this.round(amount + amount * this.countryTaxRate, 2),
                    });
                }
            }
            return deadlines;
        },
        deadlineUnchanged() {
            if (!this.cartEarlyRenewal) {
                return 0;
            }

            let discount = this.centsToCurrency(this.cartEarlyRenewalDiscount);
            let deadlinesUnchanges = 0;
            for (let month = 0; month < 12; month++) {
                let amount = this.cartTotalAmountPerMonth;

                if (discount > amount) {
                    discount -= amount;
                    amount = 0;
                } else {
                    amount -= discount;
                    discount = 0;
                }

                if (amount === this.cartTotalAmountPerMonth) {
                    deadlinesUnchanges++;
                }
            }

            return deadlinesUnchanges;
        },

        cartBasicProductTotalAmountPerMonth() {
            return this.centsToCurrency(this.cartBasicProduct.totalAmountPerMonth);
        },
        cartInvoiceEditionDate() {
            if (this.isCartContext('renew')) {
                return this.cartEarlyRenewal ? this.now : this.cartPeriodStartDateObj;
            }

            return this.cartPeriodStartDateObj || this.now;
        },
    },
    created() {
        this.setCart(this.cart);
        this.customerSubscribedTotal = this.buildTotal(
            this.subscribedProductsIterable,
            this.billingSettings.pricingPlanCode || this.currentPricingPlan,
        );
    },
    methods: {
        productName(item) {
            return this.pricingProducts.find(
                (c) => c.id === item.product || c.id === item.product_id,
            ).printableName;
        },
        centsToCurrency(cents) {
            return cents / 100;
        },
        buildTotalPreview(perMonth, vatPerMonth, perYear, vatPerYear, facility, perYearDiscounted) {
            return {
                perMonth: perMonth,
                vatPerMonth: vatPerMonth,
                vatIncludedPerMonth: perMonth + vatPerMonth,
                perYear: perYear,
                perYearDiscounted: perYearDiscounted,
                vatPerYear: vatPerYear,
                vatIncludedPerYear: perYearDiscounted + vatPerYear,
            };
        },
    },
};
</script>

<style scoped lang="scss">
@import '~@sellsy/styleguide/src/_variables';
.sub-preview-title {
    font-size: 17px;
    font-weight: 700;
    color: $gray-100 !important;
}
.sub-preview-subtitle {
    font-weight: 600;
    color: $gray-100 !important;
}
</style>
